import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { Alert, Audit, AuditScope } from "@/services/api/guardiansv2/types";
import { missionDisplayLabel } from "../shared/missionDisplayLabel";

export interface ScopeLabelCache {
  missionLabel: (id: string | null | undefined) => string | null;
  taskLabel: (id: string | null | undefined) => string | null;
}

interface CollectedScopeIds {
  missionIds: Set<string>;
  /** Every ``task_id`` referenced in audit/alert scope (with or without mission). */
  allTaskIds: Set<string>;
  /** Tasks with no ``mission_id`` in scope — fetched via ``getTask``. */
  orphanTaskIds: Set<string>;
}

export function collectScopeIds(audits: Audit[], alerts: Alert[]): CollectedScopeIds {
  const missionIds = new Set<string>();
  const allTaskIds = new Set<string>();
  const orphanTaskIds = new Set<string>();

  const noteScope = (scope: AuditScope) => {
    const mid = scope.mission_id;
    const tid = scope.task_id;
    if (mid) missionIds.add(mid);
    if (!tid) return;
    allTaskIds.add(tid);
    if (!mid) orphanTaskIds.add(tid);
  };

  for (const audit of audits) noteScope(audit.scope);
  for (const alert of alerts) noteScope(alert.scope);

  return { missionIds, allTaskIds, orphanTaskIds };
}

function pruneScopeMaps(
  missionIds: Set<string>,
  allTaskIds: Set<string>,
  missionLabels: Map<string, string>,
  taskLabels: Map<string, string>,
  unreachableMissions: Set<string>,
  unreachableTasks: Set<string>,
  seededMissionIds: Set<string>,
  seededTaskIds: Set<string>
): void {
  for (const id of missionLabels.keys()) {
    if (seededMissionIds.has(id)) continue;
    if (!missionIds.has(id)) missionLabels.delete(id);
  }
  for (const id of unreachableMissions) {
    if (!missionIds.has(id)) unreachableMissions.delete(id);
  }
  for (const id of taskLabels.keys()) {
    if (seededTaskIds.has(id)) continue;
    if (!allTaskIds.has(id)) taskLabels.delete(id);
  }
  for (const id of unreachableTasks) {
    if (!allTaskIds.has(id)) unreachableTasks.delete(id);
  }
}

export interface UseScopeLabelCacheOptions {
  /** Mission labels already loaded elsewhere (e.g. filter dropdown options). */
  seededMissionLabels?: ReadonlyMap<string, string>;
  /** Task labels already loaded elsewhere (e.g. active timeline scope). */
  seededTaskLabels?: ReadonlyMap<string, string>;
}

/**
 * Resolves mission and task display names for audit and alert scope links.
 *
 * Failed lookups (e.g. deleted tasks) are marked unreachable so the dashboard
 * does not retry on every audits/alerts refresh.
 */
export function useScopeLabelCache(
  audits: Audit[],
  alerts: Alert[] = [],
  { seededMissionLabels, seededTaskLabels }: UseScopeLabelCacheOptions = {}
): ScopeLabelCache {
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((n) => n + 1);

  const missionLabels = useRef(new Map<string, string>());
  const taskLabels = useRef(new Map<string, string>());
  const unreachableMissions = useRef(new Set<string>());
  const unreachableTasks = useRef(new Set<string>());
  const fetchingMissions = useRef(new Set<string>());
  const fetchingTasks = useRef(new Set<string>());
  const seededMissionIds = useRef(new Set<string>());
  const seededTaskIds = useRef(new Set<string>());

  useEffect(() => {
    if (!seededMissionLabels) return;
    let changed = false;
    for (const [id, label] of seededMissionLabels) {
      if (!id || !label) continue;
      missionLabels.current.set(id, label);
      seededMissionIds.current.add(id);
      changed = true;
    }
    if (changed) bump();
  }, [seededMissionLabels]);

  useEffect(() => {
    if (!seededTaskLabels) return;
    let changed = false;
    for (const [id, label] of seededTaskLabels) {
      if (!id || !label) continue;
      taskLabels.current.set(id, label);
      seededTaskIds.current.add(id);
      changed = true;
    }
    if (changed) bump();
  }, [seededTaskLabels]);

  useEffect(() => {
    const { missionIds, allTaskIds, orphanTaskIds } = collectScopeIds(audits, alerts);
    pruneScopeMaps(
      missionIds,
      allTaskIds,
      missionLabels.current,
      taskLabels.current,
      unreachableMissions.current,
      unreachableTasks.current,
      seededMissionIds.current,
      seededTaskIds.current
    );

    for (const mid of missionIds) {
      if (
        missionLabels.current.has(mid) ||
        unreachableMissions.current.has(mid) ||
        fetchingMissions.current.has(mid)
      ) {
        continue;
      }
      fetchingMissions.current.add(mid);
      void AgenticV2API.getMission(mid)
        .then((detail) => {
          missionLabels.current.set(detail._id, missionDisplayLabel(detail));
          for (const t of detail.tasks) {
            taskLabels.current.set(t._id, t.name);
          }
          bump();
        })
        .catch(() => {
          unreachableMissions.current.add(mid);
          missionLabels.current.delete(mid);
          bump();
        })
        .finally(() => {
          fetchingMissions.current.delete(mid);
        });
    }

    for (const tid of orphanTaskIds) {
      if (
        taskLabels.current.has(tid) ||
        unreachableTasks.current.has(tid) ||
        fetchingTasks.current.has(tid)
      ) {
        continue;
      }
      fetchingTasks.current.add(tid);
      void AgenticV2API.getTask(tid)
        .then((task) => {
          taskLabels.current.set(task._id, task.name);
          const taskMissionId = task.mission?.id;
          if (taskMissionId) {
            if (!missionLabels.current.has(taskMissionId)) {
              missionLabels.current.set(
                taskMissionId,
                task.mission?.name || "Mission"
              );
            }
            if (
              !unreachableMissions.current.has(taskMissionId) &&
              !fetchingMissions.current.has(taskMissionId)
            ) {
              fetchingMissions.current.add(taskMissionId);
              void AgenticV2API.getMission(taskMissionId)
                .then((detail) => {
                  missionLabels.current.set(detail._id, missionDisplayLabel(detail));
                  for (const t of detail.tasks) {
                    taskLabels.current.set(t._id, t.name);
                  }
                  bump();
                })
                .catch(() => {
                  unreachableMissions.current.add(taskMissionId);
                  missionLabels.current.delete(taskMissionId);
                  bump();
                })
                .finally(() => {
                  fetchingMissions.current.delete(taskMissionId);
                });
            }
          }
          bump();
        })
        .catch(() => {
          unreachableTasks.current.add(tid);
          taskLabels.current.delete(tid);
          bump();
        })
        .finally(() => {
          fetchingTasks.current.delete(tid);
        });
    }
  }, [audits, alerts]);

  const missionLabel = useCallback(
    (id: string | null | undefined) => {
      void version;
      if (!id || unreachableMissions.current.has(id)) return null;
      return missionLabels.current.get(id) ?? null;
    },
    [version]
  );

  const taskLabel = useCallback(
    (id: string | null | undefined) => {
      void version;
      if (!id || unreachableTasks.current.has(id)) return null;
      return taskLabels.current.get(id) ?? null;
    },
    [version]
  );

  return useMemo(
    () => ({ missionLabel, taskLabel }),
    [missionLabel, taskLabel]
  );
}
